"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import dayjs from "dayjs"
import { Loader2, PlusIcon, ShieldCheck as VerifiedIcon, TrashIcon } from "lucide-react"
import { parseAsBoolean, useQueryState } from "nuqs"
import React, { memo, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { BottomSheetModal } from "@/components/shared"
import { Button, Card, Checkbox, Image } from "@/components/ui"
import {
  deleteBankAccount,
  getBankAccountList,
  sendCodeVerifyWithdraw
} from "@/features/earns/api/billing.api"
import { DeleteConfirm } from "@/features/earns/components/banks"
import { ConfirmOTPWithdraw } from "@/features/earns/components/withdraws/otp-confirm"
import { WithdrawSuccessModal } from "@/features/earns/components/withdraws/request-withdraw"
import { EValidateUser, PolicyStatus } from "@/modules/helpers/enum"
import { useGetPaymentPolicyAgreement } from "@/modules/helpers/hooks"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { formatCurrency } from "@/modules/helpers/tap-utils"
import { sendHaptic } from "@/modules/helpers/utils"
import type { BankInfoAccount } from "@/modules/types"

// Types
type WithdrawModalProps = {
  visible: boolean
  onClose: () => void
  onAddBank: () => void
  getMaxBalance: () => number
  onRefresh: () => void
}

interface BankItemProps {
  bank: BankInfoAccount
  onSelect: () => void
  isSelected: boolean
}

// Constants
const MIN_AMOUNT = 20000
const SUCCESS_DELAY = 800

// Custom hook for bank account management
const useBankAccounts = () => {
  return useQuery({
    queryKey: ["bank_account_list"],
    queryFn: () => getBankAccountList()
  })
}

// Custom hook for OTP verification
const useOTPVerification = (onSuccess: () => void, onError: (err: any) => void) => {
  return useMutation({
    mutationFn: () => sendCodeVerifyWithdraw(),
    onSuccess() {
      onSuccess()
    },
    onError(err: any) {
      onError(err)
    }
  })
}

// Custom hook for withdraw form
interface WithdrawFormData {
  amount: string
}

const useWithdrawForm = () => {
  return useForm<WithdrawFormData>({
    defaultValues: {
      amount: ""
    }
  })
}

// Custom hook for bank selection
const useBankSelection = (banks: BankInfoAccount[] | undefined) => {
  const [bankSelected, setBankSelected] = useState<BankInfoAccount | null>(null)

  useEffect(() => {
    if (!banks?.length) {
      setBankSelected(null)
      return
    }

    // Check if current selected bank still exists in the banks array
    const bankExists = bankSelected && banks.find((bank) => bank.id === bankSelected.id)

    // If no bank is selected or the selected bank no longer exists, select a new one
    if (!bankExists) {
      const defaultBank = banks.find((bank) => bank.isDefault)

      setBankSelected(defaultBank || banks[0])
    }
  }, [banks, bankSelected])

  return { bankSelected, setBankSelected }
}

interface InputCashProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> {
  value: string
  onChange: (value: string) => void
  errors?: string
}

export const InputCash = React.forwardRef<HTMLInputElement, InputCashProps>(
  ({ value, onChange, errors, className, ...props }, ref) => {
    const [formattedValue, setFormattedValue] = React.useState("")

    const formatNumber = (rawValue: string) => {
      if (!rawValue) return ""
      const numericValue = rawValue.replace(/[^\d]/g, "") // chỉ lấy số
      return numericValue ? parseInt(numericValue, 10).toLocaleString("vi-VN") : ""
    }

    const handleChange = (inputValue: string) => {
      const numericValue = inputValue.replace(/[^\d]/g, "")
      setFormattedValue(formatNumber(numericValue))
      onChange(numericValue) // trả raw value (chỉ số) cho parent
    }

    React.useEffect(() => {
      setFormattedValue(formatNumber(value))
    }, [value])

    return (
      <div className="w-full flex flex-col gap-1">
        <div className="flex items-center gap-2 border rounded-xl px-3 py-2">
          <input
            {...props}
            ref={ref}
            value={formattedValue}
            onChange={(e) => handleChange(e.target.value)}
            inputMode="numeric"
            className={cn(
              "!border-none !rounded-none !outline-0 !ring-0 p-0 h-auto flex-1 text-sm placeholder:text-gray-300",
              className
            )}
          />
          <span className="text-gray-500 font-medium">VND</span>
        </div>
        {errors && <p className="text-sm text-red-500">{errors}</p>}
      </div>
    )
  }
)

InputCash.displayName = "InputCash"

interface AmountInputProps {
  amount: string
  setAmount: (value: string) => void
  maxAmount?: number
}

export const AmountInput: React.FC<AmountInputProps> = ({ amount, setAmount, maxAmount }) => {
  const numAmount = Number(amount) || 0
  const showMinError = numAmount > 0 && numAmount < MIN_AMOUNT

  return (
    <div className="space-y-2">
      <label className="text-lg font-semibold">Số tiền rút</label>
      <div className="flex items-start gap-1 py-1">
        <p className="text-gray-400 text-sm">*</p>
        <p className="text-xs text-gray-400 font-light">
          Lịch thanh toán định kỳ của ZZP là ngày 10, 20 và 30 hàng tháng. Nếu ngày thanh toán rơi
          vào các ngày cuối tuần (Thứ 7, Chủ nhật), lễ, Tết thì yêu cầu rút tiền sẽ được xử lý vào
          ngày làm việc kế tiếp.
        </p>
      </div>
      <div className="relative mt-2">
        <InputCash
          value={amount.toString()}
          onChange={(value) => setAmount(value)}
          placeholder="Nhập số tiền"
          className="h-10 rounded-2xl border-black text-xs !ring-0 !outline-none !border-none pr-20"
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 flex items-center justify-center bg-gray-100 text-xs border border-primary font-medium text-primary"
          onClick={() => setAmount(maxAmount?.toString() || "0")}
        >
          Tối đa
        </button>
      </div>
      {showMinError && (
        <p className="text-xs text-red-500 font-medium mt-1">
          * Số tiền rút tối thiểu là <span className="font-bold">{formatCurrency(MIN_AMOUNT)}</span>
        </p>
      )}
    </div>
  )
}

// Component for add bank button
const AddBankButton: React.FC<{
  onAddBank: () => void
}> = ({ onAddBank }) => (
  <div className="flex items-center justify-between py-2 border-t">
    <Button
      className="rounded-full font-normal text-sm w-full h-10 bg-gradient-primary"
      onClick={onAddBank}
    >
      <PlusIcon className="w-4 h-4 text-white" /> Thêm tài khoản
    </Button>
  </div>
)

// Component for bank list
const BankList: React.FC<{
  banks: BankInfoAccount[]
  bankSelected: BankInfoAccount | null
  onSelectBank: (bank: BankInfoAccount) => void
}> = ({ banks, bankSelected, onSelectBank }) => (
  <div className="space-y-2">
    {/* empty */}
    {!banks?.length && (
      <div className="flex items-center py-6 justify-center h-full">
        <p className="text-xs text-gray-700">Không có tài khoản ngân hàng</p>
      </div>
    )}

    {banks.map((bank: BankInfoAccount) => (
      <BankItem
        key={bank.id}
        bank={bank}
        onSelect={() => onSelectBank(bank)}
        isSelected={bankSelected?.id === bank.id}
      />
    ))}
  </div>
)

// Component for bank item
const BankItem = memo(({ bank, onSelect, isSelected }: BankItemProps) => {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const queryClient = useQueryClient()

  const { mutate: _deleteBank, isPending } = useMutation({
    mutationFn: (data: { id: string; code: string }) => deleteBankAccount(data.id, data.code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_account_list"] })
      setConfirmDelete(false)
    },
    onError: () => {
      toast.error("Có lỗi xảy ra, vui lòng thử lại!")
    }
  })

  const handleDeleteClick = () => {
    setConfirmDelete(true)
  }

  const handleCancelDelete = () => {
    setConfirmDelete(false)
  }

  const handleConfirmDelete = () => {
    setConfirmDelete(false)
  }

  return (
    <Card
      className={`flex items-center justify-between cursor-pointer transition ${
        isSelected ? "bg-primary/10 border border-primary" : "hover:bg-muted"
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between w-full p-3">
        <div className="flex items-center gap-3 flex-1 relative">
          {bank.isDefault && (
            <div className="flex items-center gap-1 absolute -top-5 -left-1 text-muted-foreground">
              <VerifiedIcon className="w-3 h-3" />
              <span className="text-xs font-medium">Mặc định</span>
            </div>
          )}

          <Image
            src={bank?.banks?.thumbnail || ""}
            alt={bank?.banks?.bankName}
            className="w-8 h-8 object-contain"
          />
          <div className="flex flex-col">
            <span className="uppercase text-sm font-semibold">{bank?.banks?.bankName}</span>
            <div className="flex items-center text-xs font-medium">
              <span>{bank?.accountHolderName}</span>
              <span className="mx-1">••••</span>
              <span>{bank?.accountNumber?.slice(-4)}</span>
            </div>
          </div>
        </div>

        <div>
          <Button variant="ghost" size="icon" onClick={handleDeleteClick}>
            <TrashIcon className="w-4 h-4" />
          </Button>
          <DeleteConfirm
            open={confirmDelete}
            onDelete={handleConfirmDelete}
            onCancel={handleCancelDelete}
            data={bank}
            isPending={isPending}
          />
        </div>
      </div>
    </Card>
  )
})
BankItem.displayName = "BankItem"

const OTP_EXPIRY_SECONDS = 90
const OTP_STORAGE_KEY = "@expired_time_otp_withdraw"

// Main component
export const WithdrawModal = memo(
  ({ visible, onClose, onAddBank, getMaxBalance, onRefresh }: WithdrawModalProps) => {
    const queryClient = useQueryClient()
    const [isConfirmOTP, setIsConfirmOTP] = useQueryState(
      "isConfirmOTPWithdrawModal",
      parseAsBoolean.withDefault(false)
    )
    const [isWithdrawSuccess, setIsWithdrawSuccess] = useQueryState(
      "isWithdrawSuccess",
      parseAsBoolean.withDefault(false)
    )

    const { data: banks } = useBankAccounts()
    const form = useWithdrawForm()

    const { bankSelected, setBankSelected } = useBankSelection(banks?.data?.data)

    // Payment policy agreement
    const { data: policyData, isLoading: isPolicyLoading } = useGetPaymentPolicyAgreement()
    const isPolicyAgreed =
      (policyData?.data?.status && policyData?.data?.type === PolicyStatus.PAYMENT) ?? false

    const [isPolicyChecked, setIsPolicyChecked] = useState(false)

    const { mutate: sendCodeVerifyWithdrawMutation, isPending } = useOTPVerification(
      () => {
        const expiryTime = dayjs(Date.now()).add(OTP_EXPIRY_SECONDS, "seconds").toString()
        localStorage.setItem(OTP_STORAGE_KEY, expiryTime)

        setIsConfirmOTP(true)
        sendHaptic()
      },
      (err) => {
        const type = err?.response?.data?.error?.type
        if (type?.includes(EValidateUser.YOU_CAN_ONLY_SEND_OTP_AFTER_60S)) {
          toast.error("Bạn chỉ có thể gửi mã xác minh sau 60 giây.")
          return
        }
        toast.error("Không thể gửi mã xác minh, vui lòng thử lại!")
      }
    )

    const amount = form.watch("amount")

    // Tính toán phí và thực nhận
    const { platformFee, tax, actualReceived } = useMemo(() => {
      const numAmount = Number(amount) || 0
      const platformFee = numAmount >= MIN_AMOUNT ? 3000 : 0
      const tax = numAmount * 0.1
      const actualReceived = Math.max(0, numAmount - platformFee - tax)
      return { platformFee, tax, actualReceived }
    }, [amount])

    const handleSuccess = () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["balance"] })
        queryClient.invalidateQueries({ queryKey: ["statistic"] })
        onRefresh()
      }, SUCCESS_DELAY)
    }

    const handleSendRequest = () => {
      sendCodeVerifyWithdrawMutation()
    }

    const handleCloseWithdrawSuccess = () => {
      setIsWithdrawSuccess(false)
      form.reset()
      onClose()
    }

    const bankList = banks?.data?.data || []
    const maxAmount = getMaxBalance?.()

    const today = dayjs()

    // Tính ngày dự kiến gần nhất (10, 20, 30)
    const nearestExpectedDate = [10, 20, 30]
      .map((d) => {
        // Nếu hôm nay chưa qua ngày d -> lấy ngày d trong tháng này
        if (today.date() < d) {
          return today.date(d)
        }
        // Nếu đã qua hoặc đúng ngày d -> lấy ngày d của tháng sau
        return today.add(1, "month").date(d)
      })
      .sort((a, b) => a.diff(today) - b.diff(today))[0] // chọn ngày gần nhất

    // Kiểm tra nếu ngày rơi vào cuối tuần
    const dayOfWeek = nearestExpectedDate.day() // 0 = CN, 6 = Thứ 7
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    // Nếu cuối tuần, chuyển sang ngày làm việc tiếp theo (Thứ 2)
    const adjustedDate = isWeekend
      ? nearestExpectedDate.add(dayOfWeek === 0 ? 1 : 2, "day") // CN +1, Thứ 7 +2
      : nearestExpectedDate

    const weekendNote = isWeekend
      ? `(vì ngày ${nearestExpectedDate.format("DD/MM")} là ngày cuối tuần)`
      : undefined

    return (
      <BottomSheetModal isVisible={visible} setIsVisible={onClose} title="Chọn ngân hàng nhận">
        <div className="flex flex-col gap-4 py-4 px-2">
          <ConfirmOTPWithdraw
            visible={isConfirmOTP}
            onClose={() => setIsConfirmOTP(false)}
            onSuccess={() => {
              setIsWithdrawSuccess(true)
              setIsConfirmOTP(false)
              handleSuccess()
              sendHaptic()
            }}
            data={{
              bankId: bankSelected?.id || "",
              amount: Number(amount) || 0
            }}
            shouldSavePolicy={!isPolicyAgreed && isPolicyChecked}
          />

          <WithdrawSuccessModal
            open={isWithdrawSuccess}
            onClose={handleCloseWithdrawSuccess}
            onConfirm={handleCloseWithdrawSuccess}
            amount={Math.round(Number(amount))}
            expectedDate={adjustedDate.format("DD/MM/YYYY")}
            weekendNote={weekendNote}
          />

          <BankList banks={bankList} bankSelected={bankSelected} onSelectBank={setBankSelected} />

          {bankList?.length < 3 && <AddBankButton onAddBank={onAddBank} />}

          {/* <div className="bg-red-100 text-red-500 p-2 rounded-lg flex items-center gap-2">
            <PiWarningCircleFill size={18} color="red" />
            <p className="text-xs font-normal">
              Chi phí cho mỗi lần rút là 5.000 VNĐ và sẽ được khấu trừ vào số tiền rút của bạn.
            </p>
          </div> */}

          <Controller
            name="amount"
            control={form.control}
            render={({ field }) => (
              <AmountInput amount={field.value} setAmount={field.onChange} maxAmount={maxAmount} />
            )}
          />
          {(Number(amount) || 0) >= MIN_AMOUNT && (
            <div className="flex flex-col gap-1 items-end">
              <p className="text-sm font-light">Phí nền tảng: {formatCurrency(platformFee)}</p>
              <p className="text-sm font-light">Thuế TNCN (10%): {formatCurrency(tax)}</p>
              <p className="text-sm font-medium">
                Thực nhận:{" "}
                <span className="font-semibold text-primary-800">
                  {formatCurrency(actualReceived)}
                </span>
              </p>
            </div>
          )}

          {/* Payment Policy Checkbox - only show if not agreed */}
          {!isPolicyAgreed && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="payment-policy"
                checked={isPolicyChecked}
                onCheckedChange={(checked) => setIsPolicyChecked(checked === true)}
              />
              <label htmlFor="payment-policy" className="text-sm leading-tight cursor-pointer">
                Đồng ý với{" "}
                <Link to="/payment-policy" className="text-primary underline font-medium">
                  Chính sách thanh toán
                </Link>
              </label>
            </div>
          )}
          <Button
            onClick={handleSendRequest}
            disabled={
              isPolicyLoading ||
              !amount ||
              Number(amount) > maxAmount ||
              Number(amount) < MIN_AMOUNT ||
              isPending ||
              !bankSelected ||
              (!isPolicyAgreed && !isPolicyChecked)
            }
            className="w-full !h-12 rounded-xl bg-primary hover:opacity-90"
          >
            {(isPolicyLoading || isPending) && <Loader2 className="animate-spin" />}
            Yêu cầu rút tiền
          </Button>
        </div>
      </BottomSheetModal>
    )
  }
)

WithdrawModal.displayName = "WithdrawModal"
